import type { AppointmentWithDetails } from "@/hooks/useAppointments";

export interface OwnerAddress {
  street?: string | null;
  number?: string | null;
  neighborhood?: string | null;
  zip_code?: string | null;
  complement?: string | null;
  is_primary?: boolean | null;
  deleted_at?: string | null;
  city?: { name?: string | null; state?: { code?: string | null } | null } | null;
}

/** Primary (or first non-deleted) address from the appointment's owner. */
export function getPrimaryOwnerAddress(
  appointment: Pick<AppointmentWithDetails, "owner">,
): OwnerAddress | null {
  const maybeAddresses = (appointment?.owner as { addresses?: unknown } | null | undefined)?.addresses;
  const addresses = Array.isArray(maybeAddresses)
    ? (maybeAddresses as OwnerAddress[]).filter((address) => !address?.deleted_at)
    : [];
  return addresses.find((a) => a?.is_primary) ?? addresses[0] ?? null;
}

/** Neighborhood label for the Endereço column; empty string when unknown. */
export function formatOwnerNeighborhood(address: OwnerAddress | null): string {
  return address?.neighborhood?.trim() ?? "";
}

/** Full street address ("Rua X, 123 - Bairro, Cidade/UF"); empty string when unknown. */
export function formatOwnerAddressFull(address: OwnerAddress | null): string {
  if (!address) return "";

  const streetLine = [address.street?.trim(), address.number?.trim()]
    .filter(Boolean)
    .join(", ");
  const cityState = [address.city?.name?.trim(), address.city?.state?.code?.trim()]
    .filter(Boolean)
    .join("/");
  const locality = [address.neighborhood?.trim(), cityState].filter(Boolean).join(", ");

  return [streetLine, locality].filter(Boolean).join(" - ");
}

export interface OwnerPhone {
  id?: string;
  phone_number?: string | null;
  is_primary?: boolean | null;
  is_whatsapp?: boolean | null;
  deleted_at?: string | null;
}

/** Active (non-deleted) phones from the appointment's owner, primary first. */
export function getOwnerPhones(
  appointment: Pick<AppointmentWithDetails, "owner">,
): OwnerPhone[] {
  const owner = appointment?.owner as { phones?: unknown; owners_phones?: unknown } | null | undefined;
  const maybePhones = owner?.phones ?? owner?.owners_phones;
  const phones = Array.isArray(maybePhones)
    ? (maybePhones as OwnerPhone[]).filter((phone) => !phone?.deleted_at && phone?.phone_number)
    : [];
  return [...phones].sort((a, b) => Number(b.is_primary) - Number(a.is_primary));
}
